/**
 * CaseTimeline - Visual timeline showing case history and milestones
 * Used in Attorney Case Detail page to show case progression
 */

import React from 'react';
import { 
  FileText, 
  CheckCircle, 
  Send, 
  Download, 
  Eye,
  Clock,
  AlertCircle,
  Calendar
} from 'lucide-react';

interface TimelineEvent {
  id: string;
  type: 'created' | 'released' | 'viewed' | 'downloaded' | 'updated' | 'submitted';
  title: string;
  description?: string;
  timestamp: string;
  actor?: string;
}

interface CaseTimelineProps {
  events: TimelineEvent[];
  className?: string;
}

const eventIcons: Record<string, React.ReactNode> = {
  created: <FileText className="w-4 h-4" />,
  released: <Send className="w-4 h-4" />,
  viewed: <Eye className="w-4 h-4" />,
  downloaded: <Download className="w-4 h-4" />,
  updated: <Clock className="w-4 h-4" />,
  submitted: <CheckCircle className="w-4 h-4" />
};

const eventColors: Record<string, { bg: string; border: string; text: string }> = {
  created: { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-600' },
  released: { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-600' },
  viewed: { bg: 'bg-gray-50', border: 'border-gray-400', text: 'text-gray-600' },
  downloaded: { bg: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-600' },
  updated: { bg: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-600' },
  submitted: { bg: 'bg-emerald-50', border: 'border-emerald-500', text: 'text-emerald-600' }
};

function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function getRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

export default function CaseTimeline({ events, className = '' }: CaseTimelineProps) {
  if (!events || events.length === 0) {
    return (
      <div className={`flex items-center justify-center py-8 text-gray-500 ${className}`}>
        <AlertCircle className="w-5 h-5 mr-2" />
        <span>No timeline events available</span>
      </div>
    );
  }

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <div className={`relative ${className}`} data-testid="case-timeline">
      {/* Timeline line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
      
      {/* Events */}
      <div className="space-y-4">
        {sortedEvents.map((event, index) => {
          const colors = eventColors[event.type] || eventColors.updated;
          const icon = eventIcons[event.type] || <Clock className="w-4 h-4" />;
          const isFirst = index === 0;
          
          return (
            <div 
              key={event.id} 
              className="relative pl-10"
              data-testid={`timeline-event-${event.type}`}
            >
              {/* Event dot */}
              <div 
                className={`absolute left-2 w-5 h-5 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center ${colors.text}`}
                style={{ transform: 'translateX(-50%)' }}
              >
                {icon}
              </div>
              
              {/* Event card */}
              <div 
                className={`${isFirst ? 'bg-white shadow-sm border border-gray-200' : 'bg-gray-50'} rounded-lg p-3`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${isFirst ? 'text-gray-900' : 'text-gray-700'}`}>
                        {event.title}
                      </span>
                      {isFirst && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          Latest
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-sm text-gray-500 mt-1">{event.description}</p>
                    )}
                    {event.actor && (
                      <p className="text-xs text-gray-400 mt-1">by {event.actor}</p>
                    )}
                  </div>
                  <div className="text-right ml-4 flex-shrink-0">
                    <div className="text-sm text-gray-600">{formatDate(event.timestamp)}</div>
                    <div className="text-xs text-gray-400">{formatTime(event.timestamp)}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{getRelativeTime(event.timestamp)}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper function to generate timeline events from case data
export function generateTimelineEvents(caseData: any): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  
  // Case created
  if (caseData.created_at) {
    events.push({
      id: 'created',
      type: 'created',
      title: 'Case Created',
      description: `Claim for ${caseData.client_name || 'client'} was created`,
      timestamp: caseData.created_at,
      actor: caseData.created_by_name || 'System'
    });
  }
  
  // Case released to attorney
  if (caseData.released_at) {
    events.push({
      id: 'released',
      type: 'released',
      title: 'Released to Attorney',
      description: 'Case was made available in the attorney portal',
      timestamp: caseData.released_at,
      actor: caseData.released_by_name || 'Admin'
    });
  }
  
  // Report submitted/finalized
  if (caseData.submitted_at) {
    events.push({
      id: 'submitted',
      type: 'submitted',
      title: 'Report Submitted',
      description: 'Final report was submitted',
      timestamp: caseData.submitted_at,
      actor: caseData.submitted_by_name
    });
  }
  
  // Last updated
  if (caseData.updated_at && caseData.updated_at !== caseData.created_at) {
    events.push({
      id: 'updated',
      type: 'updated',
      title: 'Case Updated',
      description: 'Case information was modified',
      timestamp: caseData.updated_at
    });
  }
  
  // Downloads (if tracked)
  if (caseData.last_downloaded_at) {
    events.push({
      id: 'downloaded',
      type: 'downloaded',
      title: 'Report Downloaded',
      description: caseData.last_download_format ? `${caseData.last_download_format.toUpperCase()} report` : 'Report downloaded',
      timestamp: caseData.last_downloaded_at,
      actor: caseData.last_downloaded_by
    });
  }
  
  // Views (if tracked)
  if (caseData.last_viewed_at) {
    events.push({
      id: 'viewed',
      type: 'viewed',
      title: 'Case Viewed',
      description: 'Case was accessed in the portal',
      timestamp: caseData.last_viewed_at
    });
  }
  
  return events;
}
